import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Plus, Send, Clock, CheckCircle, AlertCircle,
  ChevronRight, X, User, Headphones
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';

const CATEGORIES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'deposit', label: 'Deposit Issues' },
  { value: 'withdrawal', label: 'Withdrawal Issues' },
  { value: 'technical', label: 'Technical Support' },
  { value: 'kyc', label: 'KYC Verification' },
];

export default function UserSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', category: 'general' });
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await api.get('/support/tickets');
      setTickets(res.data.tickets || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/support/tickets', newTicket);
      toast.success('Ticket created successfully');
      setShowNewTicket(false);
      setNewTicket({ subject: '', message: '', category: 'general' });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/support/tickets/${selectedTicket.id}/reply`, { message: replyMessage });
      toast.success('Reply sent');
      setReplyMessage('');
      // Refresh ticket
      const res = await api.get(`/support/tickets/${selectedTicket.id}`);
      setSelectedTicket(res.data);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-500/20 text-blue-400',
      in_progress: 'bg-amber-500/20 text-amber-400',
      answered: 'bg-green-500/20 text-green-400',
      awaiting_response: 'bg-purple-500/20 text-purple-400',
      closed: 'bg-zinc-500/20 text-zinc-400',
    };
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      answered: 'Answered',
      awaiting_response: 'Awaiting Response',
      closed: 'Closed',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.open}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl text-white font-medium">Support Center</h1>
          <p className="text-sm text-zinc-500">Get help with your account</p>
        </div>
        <Button 
          onClick={() => setShowNewTicket(true)}
          className="gold-btn"
          data-testid="new-ticket-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Ticket
        </Button>
      </div>

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Headphones className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">No support tickets yet</p>
          <Button onClick={() => setShowNewTicket(true)} className="gold-btn">
            Create Your First Ticket
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket, idx) => (
            <motion.div
              key={ticket.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => setSelectedTicket(ticket)}
              className="glass-card p-4 cursor-pointer hover:border-amber-500/30 transition-colors border border-transparent"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusBadge(ticket.status)}
                    <span className="text-xs text-zinc-500 capitalize">{ticket.category}</span>
                  </div>
                  <h3 className="text-white font-medium truncate">{ticket.subject}</h3>
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-1">{ticket.message}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-xs text-zinc-500">
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </span>
                  <span className="text-xs text-zinc-400">
                    {ticket.replies?.length || 0} replies
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* New Ticket Modal */}
      <AnimatePresence>
        {showNewTicket && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowNewTicket(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 rounded-2xl p-6 max-w-lg w-full border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg text-white font-medium">Create Support Ticket</h2>
                <button onClick={() => setShowNewTicket(false)} className="text-zinc-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-400 mb-2 block">Category</label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                    className="w-full p-3 bg-zinc-900 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-amber-500/50"
                    data-testid="ticket-category"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value} className="bg-zinc-900 text-white">{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs text-zinc-400 mb-2 block">Subject</label>
                  <input
                    type="text"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    placeholder="Brief description of your issue"
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/50"
                    data-testid="ticket-subject"
                  />
                </div>

                <div>
                  <label className="text-xs text-zinc-400 mb-2 block">Message</label>
                  <textarea
                    value={newTicket.message}
                    onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                    placeholder="Describe your issue in detail..."
                    rows={5}
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 resize-none"
                    data-testid="ticket-message"
                  />
                </div>

                <Button type="submit" disabled={submitting} className="w-full gold-btn" data-testid="submit-ticket">
                  {submitting ? 'Creating...' : 'Create Ticket'}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {selectedTicket && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedTicket(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(selectedTicket.status)}
                      <span className="text-xs text-zinc-500 capitalize">{selectedTicket.category}</span>
                    </div>
                    <h2 className="text-lg text-white font-medium">{selectedTicket.subject}</h2>
                    <p className="text-xs text-zinc-500 mt-1">
                      Created {new Date(selectedTicket.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button onClick={() => setSelectedTicket(null)} className="text-zinc-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Original message */}
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <div className="bg-white/5 rounded-lg p-4">
                      <p className="text-sm text-zinc-300">{selectedTicket.message}</p>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      {new Date(selectedTicket.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Replies */}
                {selectedTicket.replies?.map((reply) => (
                  <div key={reply.id} className={`flex gap-3 ${reply.is_admin ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      reply.is_admin ? 'bg-green-500/20' : 'bg-amber-500/20'
                    }`}>
                      {reply.is_admin ? (
                        <Headphones className="w-4 h-4 text-green-400" />
                      ) : (
                        <User className="w-4 h-4 text-amber-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className={`rounded-lg p-4 ${
                        reply.is_admin ? 'bg-green-500/10 border border-green-500/20' : 'bg-white/5'
                      }`}>
                        {reply.is_admin && (
                          <p className="text-xs text-green-400 font-medium mb-2">Support Team</p>
                        )}
                        <p className="text-sm text-zinc-300">{reply.message}</p>
                      </div>
                      <p className={`text-xs text-zinc-500 mt-1 ${reply.is_admin ? 'text-right' : ''}`}>
                        {new Date(reply.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && (
                <div className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-1 p-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/50"
                      onKeyPress={(e) => e.key === 'Enter' && handleReply()}
                    />
                    <Button onClick={handleReply} disabled={submitting} className="gold-btn px-4">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
